import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TouchRipple, {
  TouchRippleActions,
} from "@mui/material/ButtonBase/TouchRipple";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";

import { useCopyToClipboard } from "../../common/CopyablePanel";
import { FieldState, getFieldTone } from "./inlineTones";
import { useGridSpan } from "./FieldGroup";

export interface InlineFieldProps {
  label: string;
  icon?: React.ReactElement;
  /** What the field shows in view mode. */
  value: React.ReactNode;
  /** Raw text copied to the clipboard; empty → not copyable. */
  copyValue?: string | number | null;
  /** No value: a muted placeholder replaces `value`. */
  empty?: boolean;
  state?: FieldState;
  /** Calculated / auto-filled value (blue). */
  auto?: boolean;
  /** How the value is calculated, shown on the "auto" badge. */
  autoHint?: string;
  /** Can be turned into an input (pencil). */
  editable?: boolean;
  /** Renders `input` instead of `value`. */
  editing?: boolean;
  /** Opened on its own: shows its own save/cancel buttons. */
  solo?: boolean;
  /** Solo mode offers a cancel (✕) that closes the input. */
  cancelable?: boolean;
  saving?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  /** Reverts a pending change without closing anything. */
  onRevert?: () => void;
  input?: React.ReactNode;
  error?: string;
  helper?: React.ReactNode;
  /** Columns taken in its FieldGroup grid (`"full"` = whole row). */
  span?: number | "full";
  mono?: boolean;
}

const actionSx = { p: 0.4, borderRadius: 1.5 } as const;

const InlineField: React.FC<InlineFieldProps> = ({
  label,
  icon,
  value,
  copyValue,
  empty = false,
  state = "idle",
  auto = false,
  autoHint,
  editable = false,
  editing = false,
  solo = false,
  cancelable = true,
  saving = false,
  onEdit,
  onSave,
  onCancel,
  onRevert,
  input,
  error,
  helper,
  span = 1,
  mono = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const copy = useCopyToClipboard();
  const rippleRef = React.useRef<TouchRippleActions>(null);
  const gridColumn = useGridSpan(span);
  const tone = getFieldTone(theme, state, auto);

  const copyText =
    copyValue === null || copyValue === undefined ? "" : String(copyValue);
  const copyable = !editing && !empty && copyText.trim().length > 0;
  const isDirty = state === "dirty";
  const showInput = editing && input !== undefined;

  const stop =
    (fn?: () => void) =>
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      fn?.();
    };

  // Click feedback of the whole card when it copies. A press on one of its own
  // buttons (copy, edit, revert) only ripples that button.
  const startRipple = (event: React.MouseEvent | React.TouchEvent): void => {
    if ((event.target as HTMLElement).closest("button, a")) return;
    rippleRef.current?.start(event);
  };
  const stopRipple = (event: React.SyntheticEvent): void => {
    rippleRef.current?.stop(event);
  };

  // Enter in a single-line input saves. Skipped for textareas (new line),
  // for an open autocomplete list (Enter picks the option) and while saving.
  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || event.shiftKey || event.defaultPrevented) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.tagName !== "INPUT") return;
    if (target.getAttribute("aria-expanded") === "true") return;
    if (saving || !onSave) return;
    event.preventDefault();
    onSave();
  };

  return (
    <Box
      sx={{
        gridColumn,
        minWidth: 0,
        position: "relative",
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        transition: "background-color .2s, border-color .2s",
        ...(state === "idle" &&
          !auto &&
          !showInput && {
            "&:hover": { bgcolor: "action.hover" },
          }),
        ...(showInput && {
          bgcolor: state === "idle" ? "background.paper" : tone.bg,
          borderColor: state === "idle" ? theme.palette.divider : tone.border,
        }),
        ...(copyable && { cursor: "copy" }),
        "&:hover .field-action, &:focus-within .field-action": { opacity: 1 },
      }}
      {...(copyable && {
        role: "button",
        tabIndex: 0,
        title: t("common.copyToClipboard", "Copy to clipboard"),
        onClick: () => copy(copyText),
        onMouseDown: startRipple,
        onMouseUp: stopRipple,
        onMouseLeave: stopRipple,
        onTouchStart: startRipple,
        onTouchEnd: stopRipple,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            rippleRef.current?.pulsate();
            copy(copyText);
          }
        },
      })}
    >
      {copyable && <TouchRipple ref={rippleRef} center={false} />}
      {/* Label row: icon + label + badges · actions */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minHeight: 26, color: "text.secondary" }}
      >
        {icon && (
          <Box
            sx={{
              display: "flex",
              color: state === "idle" && !auto ? "text.secondary" : tone.accent,
              "& svg": { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="caption"
          noWrap
          title={label}
          sx={{ fontWeight: 600, letterSpacing: 0.2, minWidth: 0 }}
        >
          {label}
        </Typography>
        {auto && (
          <Tooltip
            arrow
            title={
              autoHint
                ? `${t("inlineEdit.autoValue", "Calculado automáticamente")}: ${autoHint}`
                : t("inlineEdit.autoValue", "Calculado automáticamente")
            }
          >
            <AutoAwesomeIcon
              sx={{ fontSize: 13, color: theme.palette.info.main }}
            />
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {/* View mode: copy, then the pencil to its right */}
        {!editing && (
          <Stack direction="row" spacing={0.25} alignItems="center">
            {isDirty && onRevert && (
              <Tooltip title={t("inlineEdit.revert", "Deshacer cambio")}>
                <IconButton
                  size="small"
                  onClick={stop(onRevert)}
                  sx={{ ...actionSx, color: tone.text }}
                >
                  <UndoIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {copyable && (
              <Tooltip title={t("common.copy", "Copy")}>
                <IconButton
                  size="small"
                  className="field-action"
                  onClick={stop(() => copy(copyText))}
                  sx={{ ...actionSx, opacity: 0, transition: "opacity .15s" }}
                >
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {editable && onEdit && (
              <Tooltip title={t("common.edit", "Edit")}>
                <IconButton
                  size="small"
                  className="field-action"
                  onClick={stop(onEdit)}
                  aria-label={`${t("common.edit", "Edit")} ${label}`}
                  sx={{
                    ...actionSx,
                    opacity: isDirty ? 1 : 0,
                    transition: "opacity .15s",
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}

        {/* Edit mode, opened on its own: save (only with a change) + cancel */}
        {showInput && solo && (
          <Stack direction="row" spacing={0.25} alignItems="center">
            {isDirty && onSave && (
              <Tooltip title={t("common.save", "Save")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={stop(onSave)}
                    disabled={saving}
                    aria-label={`${t("common.save", "Save")} ${label}`}
                    sx={{
                      ...actionSx,
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.common.white,
                      "&:hover": { bgcolor: theme.palette.success.dark },
                    }}
                  >
                    {saving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {cancelable ? (
              <Tooltip title={t("common.cancel", "Cancel")}>
                <IconButton
                  size="small"
                  onClick={stop(onCancel)}
                  disabled={saving}
                  aria-label={`${t("common.cancel", "Cancel")} ${label}`}
                  sx={actionSx}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : (
              isDirty &&
              onRevert && (
                <Tooltip title={t("inlineEdit.revert", "Deshacer cambio")}>
                  <IconButton
                    size="small"
                    onClick={stop(onRevert)}
                    disabled={saving}
                    sx={{ ...actionSx, color: tone.text }}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Stack>
        )}

        {/* Edit mode via "edit all": just a way back for a changed field */}
        {showInput && !solo && isDirty && onRevert && (
          <Tooltip title={t("inlineEdit.revert", "Deshacer cambio")}>
            <IconButton
              size="small"
              onClick={stop(onRevert)}
              sx={{ ...actionSx, color: tone.text }}
            >
              <UndoIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Value / input */}
      <Box
        sx={{ mt: 0.5 }}
        onClick={showInput ? (event) => event.stopPropagation() : undefined}
        onKeyDown={showInput ? handleInputKeyDown : undefined}
      >
        {showInput ? (
          input
        ) : (
          <Typography
            component="div"
            variant="body1"
            sx={{
              color: empty ? "text.disabled" : tone.text,
              fontWeight: isDirty ? 700 : 500,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              lineHeight: 1.45,
              fontFamily: mono && !empty ? "monospace" : undefined,
              fontSize: mono && !empty ? "0.85rem" : undefined,
            }}
          >
            {empty ? "—" : value}
          </Typography>
        )}
      </Box>

      {(error || helper) && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: 0.5,
            color: error ? "error.main" : "text.secondary",
          }}
        >
          {error || helper}
        </Typography>
      )}
    </Box>
  );
};

export default InlineField;
